/**
 * 决策树 tab — canvas or outline, read-only until you press Edit.
 *
 * Laid out after the design mock (Figma `cpa-liu`, 2026-10-29):
 *
 *   ┌ Container ──────────────────────────────────────────────────────────┐
 *   │ 决策树             ◉ 画布  ○ 大纲              [✎ Edit]            │ ← read
 *   │ 决策树             ◉ 画布  ○ 大纲        Cancel  [ Save ]          │ ← edit
 *   ├─────────────────────────────────────────────────────────────────────┤
 *   │ (edit only)  ＋问题 | ⊞子集 | ＋判断 | 🗑Delete                      │
 *   │   canvas (bg #F0FBFF while editing)   /   outline table             │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * ## Why read-only is the default
 *
 * The mock separates viewing from editing, and for this page that is the right call:
 * these rules decide which investment programs a seller is told to pursue, so
 * entering edit mode should be a decision rather than the resting state. The first
 * cut was always-editable with permissions merely disabling controls, which made an
 * accidental drag indistinguishable from an intended one.
 *
 * The edit-mode canvas background (`#F0FBFF` — Cloudscape's
 * `color-background-status-info`) is the mock's idea and a good one: the mode is
 * visible without reading the buttons.
 *
 * ## Nothing persists
 *
 * Save is disabled. `SalesRecommendationHub` has no API that writes a questionnaire
 * definition — the five `q_1_*.json` files are read off the Lambda's classpath in a
 * `static {}` block. The button is rendered because the mock has it and because it is
 * where the publish-time topology gate belongs, but the banner says why it cannot act
 * rather than the press failing silently.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Container,
  FormField,
  Input,
  Modal,
  RadioGroup,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react';

import SafeTrans from '../../../i18n/SafeTrans';
import EdgeInspector from '../../components/EdgeInspector/EdgeInspector';
import NodeInspector from '../../components/NodeInspector/NodeInspector';
import OutlineView from '../../components/OutlineView/OutlineView';
import TreeCanvas from '../../components/TreeCanvas/TreeCanvas';
import { SEED_BY_ID } from '../../fixtures/questionnaires';
import { draftReducer, initDraft } from '../../state/draftReducer';
import { clearPositions, loadPositions, savePositions } from '../../state/layoutStorage';
import { cpaSaveLog } from '../../utils/saveLog';
import { documentSha256, serializeDocument, toDocument } from '../../utils/wire';
import type { ToDocumentResult } from '../../utils/wire';
import { getDecisionTreeApi } from '../../client/apiProvider';
import {
  DecisionTreeConflictError,
  DecisionTreeValidationError,
  NotFoundError,
  type DecisionTreeDraftData,
  type DecisionTreeEditingContext,
} from '../../client/treeApiTypes';
import { type NewNodeKind } from '../../utils/graphEdit';
import {
  branchExpressionFor,
  canAddBranch,
  connectionKind,
  simulateDelete,
  unusedOptionValues,
} from '../../utils/graphEdit';
import { type Ordering, projectQuestionnaire } from '../../utils/projection';
import './CanvasPage.css';

/**
 * The WHOLE questionnaire, as one graph.
 *
 * `q_all` is the merged walk: the two questions AgentHubPortal hard-codes in
 * `staticQuestions.tsx` (SFDC account, then purpose), followed by all five
 * `q_1_*.json` trees hanging off the purpose answer. That is what a BD actually
 * walks — it had never existed as a single artefact, because the first two
 * questions live in a React file and the rest on a Lambda's classpath.
 *
 * The five individual trees are still in the fixtures and still addressable by id;
 * the picker is gone from the UI because this merged view supersedes it.
 *
 * Module scope so the reducer's initial state and the layout-storage key cannot
 * disagree about which tree this page is showing.
 */
const DEFINITION_ID = 'q_all';

/**
 * Whether the canvas draws the derived eligibility boxes.
 *
 * `hide` to match the mock, which shows only question cards and connectors. On the
 * merged tree the grant column is 46 of 76 boxes, so it dominated a view whose job is
 * the question flow. The rules themselves are untouched — still on the nodes, still
 * checked, still shown in the inspector. One line to put them back.
 */
const SHOW_GRANTS: 'show' | 'hide' = 'hide';

export interface CanvasPageProps {
  canEdit: boolean;
  readOnlyReason: string | null;
}

export default function CanvasPage({ canEdit, readOnlyReason }: CanvasPageProps) {
  const { t } = useTranslation('cpa');
  const definitionId = DEFINITION_ID;
  // Initialised from the SAME constant as `definitionId`. It used to be a literal
  // `SEED_BY_ID.q_1_1` here, which meant switching the page to `q_all` changed only
  // the layout-storage key while the canvas went on rendering q_1_1 — the draft is
  // what gets drawn. `switchDefinition` was the one thing that re-pointed the
  // reducer, and it went away with the tree picker.
  const [state, dispatch] = useReducer(draftReducer, SEED_BY_ID[DEFINITION_ID], initDraft);
  const [view, setView] = useState<'canvas' | 'outline'>('canvas');
  /** Read-only until Edit is pressed, per the mock. */
  const [editing, setEditing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  /** Filled by the canvas; read at click time. See `ViewportCentre` in TreeCanvas. */
  const viewportCentre = useRef<(() => { x: number; y: number }) | null>(null);
  const [ordering, setOrdering] = useState<Ordering>('source');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [pendingBranch, setPendingBranch] = useState<string | null>(null);
  const [branchValue, setBranchValue] = useState<string | null>(null);
  const [branchExpr, setBranchExpr] = useState('');
  /**
   * Where the new rule points: a question this creates, or one already on the canvas.
   *
   * Both used to exist as separate gestures — ＋规则 made a new question, and dragging
   * between two handles connected existing ones. The drag is gone (see `connectable`),
   * so the choice it used to express has to live here or the capability is lost.
   */
  const [branchTarget, setBranchTarget] = useState<'new' | 'existing'>('new');
  const [branchTargetId, setBranchTargetId] = useState<string | null>(null);
  /**
   * ＋规则 armed: every card's handles are live and a drag between two of them creates
   * the edge.
   *
   * A mode rather than an always-on gesture, because an edge was the one thing on this
   * canvas you could create by accident — the handles sit on the card border, so a drag
   * that starts a few pixels off moves nothing and connects instead. A mode rather than
   * a selection, because requiring a selected card first asked for the source before the
   * author had looked at the graph; here the gesture names both ends.
   */
  const [ruleMode, setRuleMode] = useState(false);
  /** A drag that landed on a target the model refuses, so it can be said out loud. */
  const [connectError, setConnectError] = useState<string | null>(null);
  /**
   * Canvas filling the window.
   *
   * The merged tree is ~2600 x 1350. In the page's normal 560–800px pane that fits only
   * at a zoom where the question text is unreadable, so reading the graph and reading a
   * card were mutually exclusive. This is a CSS state, not a browser fullscreen request:
   * the Fullscreen API would take the dialogs and the inspector's `expandToViewport`
   * dropdowns out of the rendered tree with it.
   */
  const [maximized, setMaximized] = useState(false);

  const projection = useMemo(
    () => projectQuestionnaire(state.draft, ordering, { grants: SHOW_GRANTS, layout: 'tidy' }),
    [state.draft, ordering],
  );
  const selected = useMemo(
    () => projection.nodes.find((n) => n.id === selectedId) ?? null,
    [projection.nodes, selectedId],
  );
  const selectedEdge = useMemo(
    () => projection.edges.find((e) => e.id === selectedEdgeId) ?? null,
    [projection.edges, selectedEdgeId],
  );
  const deleteImpact = useMemo(
    () => (pendingDelete ? simulateDelete(state.draft, pendingDelete) : null),
    [pendingDelete, state.draft],
  );
  const branchOptions = useMemo(
    () => (pendingBranch ? unusedOptionValues(state.draft, pendingBranch) : []),
    [pendingBranch, state.draft],
  );
  const branchGuard = useMemo(
    () => (pendingBranch ? canAddBranch(state.draft, pendingBranch) : null),
    [pendingBranch, state.draft],
  );
  const branchNode = useMemo(
    () => state.draft.nodes.find((n) => n.nodeId === pendingBranch) ?? null,
    [pendingBranch, state.draft],
  );
  /**
   * Questions this rule could legally point at.
   *
   * Filtered by the same `connectionKind` guard the drag gesture used, so an illegal
   * target is absent rather than offered and then rejected — the drag's failure mode was
   * a banner after the fact, which is the worst time to learn it.
   */
  const branchTargets = useMemo(() => {
    if (!pendingBranch) return [];
    return state.draft.nodes
      .filter((n) => n.nodeId !== pendingBranch)
      .filter((n) => connectionKind(state.draft, pendingBranch, n.nodeId).kind !== 'blocked')
      .map((n) => ({
        value: n.nodeId,
        label: n.questionText.cn || n.questionText.en,
        description: n.nodeId,
      }));
  }, [pendingBranch, state.draft]);

  /**
   * An unconditional edge needs no expression — the source has no options left to
   * branch on, so the rule is "always go here".
   */
  const branchIsUnconditional =
    !!pendingBranch &&
    branchTarget === 'existing' &&
    !!branchTargetId &&
    connectionKind(state.draft, pendingBranch, branchTargetId).kind === 'unconditional';

  const effectiveBranchExpr =
    pendingBranch && branchValue
      ? branchExpressionFor(state.draft, pendingBranch, branchValue)
      : branchExpr.trim();

  const movedCount = Object.keys(state.positions).length;
  const deletableSelection = selected && !selected.derived ? selected.id : null;

  /*
   * A newly created node becomes the selection, which is what opens the inspector on
   * it. The pan is TreeCanvas's job (`focusNodeId`); selecting is this page's, because
   * the inspector is rendered here.
   *
   * Keyed on the reducer's one-shot `lastAddedNodeId`, so this fires once per creation
   * and never fights a selection the user made afterwards.
   */
  useEffect(() => {
    if (!state.lastAddedNodeId) return;
    setSelectedEdgeId(null);
    setSelectedId(state.lastAddedNodeId);
  }, [state.lastAddedNodeId]);

  // Restore the saved shape on mount and on every questionnaire switch.
  useEffect(() => {
    dispatch({
      type: 'load-positions',
      positions: loadPositions(
        definitionId,
        SEED_BY_ID[definitionId].nodes.map((n) => n.nodeId),
      ),
    });
  }, [definitionId]);

  useEffect(() => {
    savePositions(definitionId, state.positions);
  }, [definitionId, state.positions]);

  /*
   * ── Backend call point ①: open ────────────────────────────────────────────
   *
   * GetDecisionTreeDraft, falling back to CreateDecisionTreeDraft on 404. The fork happens
   * server-side, so the ~24 KB document is never downloaded only to be sent straight back.
   *
   * The canvas still RENDERS from `state.draft`, which the reducer seeded from the fixtures.
   * Loading the fetched document into the reducer needs `fromDocument` (the inverse of
   * `toDocument`), which is the next piece; until then this proves the lifecycle and leaves
   * the rendering path alone rather than half-converting it.
   */
  const [draftMeta, setDraftMeta] = useState<DecisionTreeDraftData | null>(null);
  const [context, setContext] = useState<DecisionTreeEditingContext | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    const api = getDecisionTreeApi(definitionId);
    let live = true;
    void (async () => {
      try {
        let result;
        try {
          result = await api.getDraft({ treeId: definitionId });
        } catch (e) {
          if (!(e instanceof NotFoundError)) throw e;
          result = await api.createDraft({ treeId: definitionId });
        }
        if (!live) return;
        setDraftMeta(result.draft);
        setContext(result.context);
        void cpaSaveLog({
          point: 'open',
          treeId: definitionId,
          reason: `draft fork 自 v${result.draft.baseVersion}`,
          extra: {
            'draft.updatedAt': result.draft.updatedAt,
            headVersion: result.context.headVersion,
            activeVersion: result.context.activeVersion,
            otherEditors: result.context.otherEditors,
            'document bytes': result.draft.document.length,
          },
        });
      } catch (e) {
        if (live) setApiError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [definitionId]);

  /*
   * ── Backend call point ②: autosave ───────────────────────────────────────
   *
   * Four triggers, whichever comes first. The first two are the usual pair; the third and
   * fourth are the cases they each miss:
   *
   *   3s after the last edit   debounce, so typing a question does not send per keystroke
   *   30s of continuous edits  a debounce NEVER fires while someone keeps dragging
   *   25 edits                 a fast burst (adding options) can beat both of the above
   *   blur / page hide         the only chance left before the tab goes away
   *
   * Only in edit mode, and only when something actually changed: `state.draft` identity is
   * the change signal, so a re-render from selection or zoom must not trigger a save.
   */
  const lastSavedDraft = useRef<unknown>(null);
  const editsSinceSave = useRef(0);
  const hardFlushAt = useRef<number | null>(null);

  useEffect(() => {
    if (!editing || !canEdit) return;
    if (state.draft === lastSavedDraft.current) return;

    editsSinceSave.current += 1;
    if (hardFlushAt.current === null) hardFlushAt.current = Date.now() + 30_000;

    const flush = (reason: string) => {
      const meta = draftMeta;
      if (!meta) return;
      lastSavedDraft.current = state.draft;
      editsSinceSave.current = 0;
      hardFlushAt.current = null;
      const { document, unconvertible } = toDocument(state.draft, state.positions);
      void (async () => {
        try {
          const r = await getDecisionTreeApi(definitionId).putDraft({
            treeId: definitionId,
            document: serializeDocument(document),
            schemaVersion: document.schemaVersion,
            // The draft's own updatedAt, not a wall clock: a mismatch means a SECOND TAB has
            // written, and overwriting blind loses whichever tab saved less recently.
            expectedUpdatedAt: meta.updatedAt,
          });
          setDraftMeta(r.draft);
          setContext(r.context);
          setApiError(null);
          void cpaSaveLog({
            point: 'autosave',
            treeId: definitionId,
            document,
            unconvertible,
            reason,
            extra: {
              'draft.updatedAt': r.draft.updatedAt,
              headVersion: r.context.headVersion,
              'head moved?': r.context.headVersion > meta.baseVersion,
              otherEditors: r.context.otherEditors,
            },
          });
        } catch (e) {
          // Stop autosaving. Retrying with the same stale expectedUpdatedAt would fail
          // forever, and retrying without it would be the blind overwrite this guards.
          lastSavedDraft.current = null;
          setApiError(e instanceof Error ? e.message : String(e));
        }
      })();
    };

    if (editsSinceSave.current >= 25) {
      flush('25 edits');
      return;
    }
    if (hardFlushAt.current !== null && Date.now() >= hardFlushAt.current) {
      flush('30s of continuous editing');
      return;
    }

    const debounce = window.setTimeout(() => flush('3s idle'), 3000);
    const hard = window.setTimeout(
      () => flush('30s of continuous editing'),
      Math.max(0, hardFlushAt.current - Date.now()),
    );
    const onHide = () => flush('page hidden');
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.clearTimeout(debounce);
      window.clearTimeout(hard);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [editing, canEdit, state.draft, state.positions, definitionId, draftMeta]);

  /*
   * ESC restores. Registered on `document` because the canvas is not focused after a
   * pan or a zoom — React Flow puts focus on the pane, and keying off the pane meant ESC
   * worked only if you had not touched anything.
   *
   * Guarded on `maximized` so the listener is absent the rest of the time, and skipped
   * while a dialog is open: Cloudscape's Modal closes on ESC too, and one key should not
   * dismiss two layers at once. `capture: false` leaves the Modal first in line.
   */
  useEffect(() => {
    if (!maximized && !ruleMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (pendingBranch || pendingDelete) return;
      // One layer per press, innermost first: disarming the gesture and collapsing the
      // canvas in one keystroke would undo a state the user never asked to leave.
      if (ruleMode) {
        setRuleMode(false);
        setConnectError(null);
      } else {
        setMaximized(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [maximized, ruleMode, pendingBranch, pendingDelete]);

  /*
   * Stop the page behind from scrolling while the canvas covers it. Restored to the
   * previous value rather than to '' so leaving maximized cannot clear an overflow some
   * other component set.
   */
  useEffect(() => {
    if (!maximized) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [maximized]);

  /** Leaving edit mode also drops the mode armed inside it. */
  const leaveEdit = () => {
    /*
     * ── Backend call point ④: discard ───────────────────────────────────────
     *
     * DeleteDecisionTreeDraft, with expectedUpdatedAt as a query parameter -- a second tab
     * may have written since, and discarding its work because this tab decided to is the
     * same bug as a blind autosave, in reverse.
     */
    void cpaSaveLog({
      point: 'discard',
      treeId: definitionId,
      extra: { expectedUpdatedAt: draftMeta?.updatedAt },
    });
    if (draftMeta) {
      const api = getDecisionTreeApi(definitionId);
      void api
        .deleteDraft({ treeId: definitionId, expectedUpdatedAt: draftMeta.updatedAt })
        // Re-fork straight away: without a draft the next Edit has nothing to write to.
        .then(() => api.createDraft({ treeId: definitionId }))
        .then((r) => {
          setDraftMeta(r.draft);
          setContext(r.context);
        })
        .catch((e) => setApiError(e instanceof Error ? e.message : String(e)));
    }
    setEditing(false);
    setRuleMode(false);
    setConnectError(null);
    dispatch({ type: 'discard' });
  };

  /*
   * ── Backend call point ③: commit ──────────────────────────────────────────
   *
   * CommitDecisionTreeVersion. Note what is NOT sent: the document. The draft is the staging
   * area, so the content is already stored -- sending it again would double the upload and
   * open a window where the draft and the committed bytes disagree. `documentSha256` closes
   * the one hole `expectedDraftUpdatedAt` cannot: if the final autosave silently failed, both
   * timestamps are the older value and therefore MATCH, so a stale document would be promoted
   * with the newest edits lost, reporting success the whole way.
   *
   * The real sequence is: await a forced autosave, stop on failure, then commit what landed.
   */
  const [committing, setCommitting] = useState(false);

  const commit = async () => {
    if (!draftMeta || committing) return;
    setCommitting(true);
    setApiError(null);
    const api = getDecisionTreeApi(definitionId);
    const { document, unconvertible } = toDocument(state.draft, state.positions);
    const json = serializeDocument(document);
    try {
      // Flush first, and stop on failure. The commit promotes what LANDED, so committing
      // after a failed autosave would promote the older document.
      const flushed = await api.putDraft({
        treeId: definitionId,
        document: json,
        schemaVersion: document.schemaVersion,
        expectedUpdatedAt: draftMeta.updatedAt,
      });
      setDraftMeta(flushed.draft);
      setContext(flushed.context);

      const sha = await documentSha256(json);
      const { version } = await api.commitVersion({
        treeId: definitionId,
        expectedDraftUpdatedAt: flushed.draft.updatedAt,
        // Not redundant with the timestamp: if the flush above had silently failed, both
        // timestamps would be the older value and therefore MATCH, so a stale document
        // would be promoted with the newest edits lost -- reporting success the whole way.
        documentSha256: sha,
        baseVersion: flushed.draft.baseVersion,
        note: `画布保存 · ${document.questions.length} 题 / ${document.transitions.length} 条规则`,
      });
      void cpaSaveLog({
        point: 'commit',
        treeId: definitionId,
        document,
        unconvertible,
        reason: `committed as v${version.version}`,
        extra: {
          version: version.version,
          baseVersion: version.baseVersion,
          documentSha256: sha,
          'document (NOT sent)': 'the draft already held it',
        },
      });
      // The draft became a version, so this editor needs a new one before it can edit again.
      const next = await api.createDraft({ treeId: definitionId, baseVersion: version.version });
      setDraftMeta(next.draft);
      setContext(next.context);
    } catch (e) {
      if (e instanceof DecisionTreeConflictError) {
        setApiError(
          `${e.message} 当前 head 是 v${e.headVersion}（${e.headAuthor ?? '他人'}）—— ` +
            `需要三路合并后再提交。`,
        );
      } else if (e instanceof DecisionTreeValidationError) {
        setApiError(`${e.message} ${e.problems.map((p) => p.message).join('；')}`);
      } else {
        setApiError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setCommitting(false);
    }
  };

  /**
   * Expressions this save would NOT persist, held until the author says to go ahead.
   *
   * `toDocument` is explicit that unconvertible rules are neither dropped silently nor
   * approximated — "the caller has to decide" — and until this existed the caller did not:
   * `commit()` read `unconvertible`, passed it to the log, and committed anyway. Pressing Save
   * promoted a version missing (measured on the live tree) 16 of its 30 eligibility rules, and
   * reported success. Structural validation cannot catch it, because the document it validates
   * is internally consistent; what is missing never reached it.
   */
  const [pendingUnconvertible, setPendingUnconvertible] = useState<
    ToDocumentResult['unconvertible'] | null
  >(null);

  /**
   * Save. Asks first when the document cannot carry everything the canvas holds.
   *
   * Deliberately a confirmation and not a hard block. Blocking is the obvious reading of
   * "don't lose rules", but with today's data 16 rules are unconvertible, so a block would
   * make Save permanently unreachable and the admin surface unusable — and the rules are not
   * lost by saving: they stay in the source definition, which is still the system of record
   * until the document model grows the four missing term shapes. What was unacceptable is
   * saving WITHOUT SAYING SO, so the dialog names every expression it cannot take.
   */
  const requestCommit = () => {
    if (!draftMeta || committing) return;
    const { unconvertible } = toDocument(state.draft, state.positions);
    if (unconvertible.length > 0) {
      setPendingUnconvertible(unconvertible);
      return;
    }
    void commit();
  };

  /**
   * Open the dialog on `nodeId`, optionally with the target already decided.
   *
   * `target` is set when a drag chose both ends on the canvas; left out, the dialog asks
   * — so one dialog serves both the gesture and the keyboard path.
   */
  const openBranchDialog = (nodeId: string, target?: string) => {
    setPendingBranch(nodeId);
    const opts = unusedOptionValues(state.draft, nodeId);
    setBranchValue(opts.length === 1 ? opts[0] : null);
    setBranchExpr('');
    setBranchTarget(target ? 'existing' : 'new');
    setBranchTargetId(target ?? null);
  };

  /**
   * A line was dragged from one card to another while ＋规则 was armed.
   *
   * `isValidConnection` already refused the illegal pairs during the drag, so reaching
   * here means the model allows it; the guard is re-checked anyway because the draft can
   * change under a drag in progress, and a blocked pair must not fall through to a
   * dialog that cannot commit.
   */
  const handleConnect = (source: string, target: string) => {
    const k = connectionKind(state.draft, source, target);
    if (k.kind === 'blocked') {
      setConnectError(k.reason);
      return;
    }
    setConnectError(null);
    setRuleMode(false);
    openBranchDialog(source, target);
  };

  /**
   * Commit the rule the ＋规则 dialog describes.
   *
   * Two dispatches behind one button, because from the author's side it is one act —
   * "this answer goes there" — and the only difference is whether "there" already
   * exists. Splitting it into two buttons made people pick the mechanism before they
   * had decided the intent.
   */
  const commitBranch = () => {
    if (!pendingBranch) return;
    if (branchTarget === 'existing') {
      if (!branchTargetId) return;
      dispatch({
        type: 'connect',
        sourceNodeId: pendingBranch,
        targetNodeId: branchTargetId,
        // An unconditional edge carries no expression at all; passing an empty string
        // would store a rule that evaluates to nothing.
        ...(branchIsUnconditional ? {} : { expression: effectiveBranchExpr }),
      });
      setSelectedId(null);
      setSelectedEdgeId(`e:${pendingBranch}->${branchTargetId}:0`);
    } else if (effectiveBranchExpr) {
      dispatch({ type: 'add-branch', fromNodeId: pendingBranch, expression: effectiveBranchExpr });
    }
    setPendingBranch(null);
  };

  return (
    <SpaceBetween size="m">
      {readOnlyReason && <Alert type="info">{readOnlyReason}</Alert>}

      {/*
       * The title row is rendered as the first row of the BODY rather than through
       * Container's `header` slot. In the mock it is a bar in its own right — fixed
       * ~48px, white even when the canvas below is tinted, closed by a 1px divider.
       * Container's header slot adds its own vertical padding and no divider, which
       * made the row twice as tall as the mock's and let it blend into the canvas.
       * Owning the markup is what makes the measured height reachable.
       */}
      {/* The maximize state is a class on a plain wrapper OUTSIDE Container: `position:
          fixed` has to apply to an element Cloudscape does not own, or a future change to
          Container's own positioning silently breaks it. */}
      <div className={maximized ? 'cpa-ct-wrap cpa-ct-wrap--max' : 'cpa-ct-wrap'}>
      <Container disableContentPaddings>
        <div className={`cpa-ct-body ${editing && view === 'canvas' ? 'cpa-ct-body--edit' : ''}`}>
          <div className="cpa-ct-head">
            <h2 className="cpa-ct-head__title">{t('treeTitle')}</h2>
            <div className="cpa-ct-head__right">
              {/* `direction` is a first-class RadioGroup prop — the mock's inline
                  layout needs no CSS. An earlier override tried to force the row with
                  a selector on the generated class name and only squeezed the labels
                  into mid-word wraps. */}
              <RadioGroup
                direction="horizontal"
                value={view}
                onChange={({ detail }) => setView(detail.value as 'canvas' | 'outline')}
                items={[
                  { value: 'canvas', label: t('viewCanvas') },
                  { value: 'outline', label: t('viewOutline') },
                ]}
              />
              {/* Only on the canvas: the outline is a list and already fits. */}
              {view === 'canvas' && (
                <Button
                  iconName={maximized ? 'exit-full-screen' : 'full-screen'}
                  onClick={() => setMaximized((v) => !v)}
                  // The ESC hint belongs on the control that ESC undoes — a key with no
                  // visible affordance is one nobody finds.
                  ariaLabel={maximized ? '还原画布（ESC）' : '最大化画布'}
                >
                  {maximized ? '还原 (ESC)' : '最大化'}
                </Button>
              )}
              {editing ? (
                <SpaceBetween direction="horizontal" size="s">
                  <Button variant="link" onClick={leaveEdit}>
                    {t('cancel')}
                  </Button>
                  <Button variant="primary" loading={committing} onClick={requestCommit}>
                    {t('save')}
                  </Button>
                </SpaceBetween>
              ) : (
                <Button iconName="edit" disabled={!canEdit} onClick={() => setEditing(true)}>
                  {t('edit')}
                </Button>
              )}
            </div>
          </div>

          {editing && (
            <Alert type="warning" className="cpa-ct-alert" header={t('draftBannerTitle')}>
              {t('draftBannerBody')}
            </Alert>
          )}

          {view === 'outline' ? (
            <div className="cpa-ct-pane">
              <OutlineView
                definition={state.draft}
                onSelectNode={(id) => {
                  setView('canvas');
                  setSelectedEdgeId(null);
                  setSelectedId(id);
                }}
              />
            </div>
          ) : (
            <>
              {editing && (
                <div className="cpa-toolbar">
                  {/*
                   * Create BY KIND, not "create then convert".
                   *
                   * A numeric question needs `questionType`, an empty option list and
                   * `dataType: NUMBER`; getting the third wrong leaves a TEXT node whose
                   * rules do arithmetic — one of the two silent breakages the checker
                   * exists to catch. Picking the kind up front mints a node that is
                   * already valid.
                   *
                   * Four items, because the model expresses four kinds. The
                   * questionnaire really uses six — `$K` amounts, ratio-backed
                   * percentages and the SFDC picker are hard-coded by id in
                   * AgentHubPortal — so they cannot be offered here honestly.
                   */}
                  <ButtonDropdown
                    /*
                     * Text, not `variant="inline-icon"`.
                     *
                     * The icon variant drops the children entirely, so the only
                     * primary action on this toolbar rendered as a bare three-dot
                     * glyph — unreadable next to the labelled buttons beside it. An
                     * overflow icon is for a menu of things you already know about;
                     * this one is how you create a question at all.
                     */
                    expandToViewport
                    items={[
                      { id: 'single', text: t('kindSingle'), description: t('kindSingleHint') },
                      { id: 'multi', text: t('kindMulti'), description: t('kindMultiHint') },
                      { id: 'number', text: t('kindNumber'), description: t('kindNumberHint') },
                      // Amount and percentage are presets of 数值, not kinds — they
                      // differ by `unit` (and `scale`, which the percentage needs to
                      // keep the stored value a ratio).
                      { id: 'amount', text: t('kindAmount'), description: t('kindAmountHint') },
                      { id: 'percent', text: t('kindPercent'), description: t('kindPercentHint') },
                      { id: 'text', text: t('kindText'), description: t('kindTextHint') },
                      { id: 'sfdc', text: t('kindSfdc'), description: t('kindSfdcHint') },
                    ]}
                    onItemClick={({ detail }) =>
                      // Dropped where the user is looking. Falling back to no `at` just
                      // lets the layout place it, which is the old behaviour.
                      dispatch({
                        type: 'add-node',
                        kind: detail.id as NewNodeKind,
                        at: viewportCentre.current?.(),
                      })
                    }
                  >
                    {t('paletteQuestion')}
                  </ButtonDropdown>
                  <span className="cpa-toolbar__sep" />
                  {/*
                   * ＋规则 stands where the mock's ＋判断 did, and it is now the ONLY way
                   * to create an edge.
                   *
                   * 判断 was a placeholder for a decision node the stored model has no
                   * room for — `QuestionnaireNode` has one type and a threshold lives
                   * inside an expression string. A rule, on the other hand, is a real
                   * record: one entry in the source question's `nextQuestionRules`. So
                   * the slot creates the thing that exists instead of explaining the
                   * thing that does not.
                   *
                   * 子集 sat next to it for the same reason 判断 did — to match the
                   * mock's palette — and has been removed: a button whose only behaviour
                   * is to explain that it does nothing is worse than its absence, since
                   * it reads as a feature until pressed.
                   *
                   * It ARMS the gesture rather than acting on the selection. Requiring a
                   * selected card first asked which question the rule leaves before the
                   * author had looked at the graph, and offered no way to say where it
                   * goes; armed, every card's handles go live and one drag names both
                   * ends. Not disabled, for the same reason — there is nothing to select
                   * first.
                   */}
                  <button
                    type="button"
                    className={`cpa-toolbar__btn ${ruleMode ? 'on' : ''}`}
                    aria-pressed={ruleMode}
                    onClick={() => {
                      setRuleMode((v) => !v);
                      setConnectError(null);
                    }}
                  >
                    {t('paletteRule')}
                  </button>
                  <span className="cpa-toolbar__sep" />
                  <button
                    type="button"
                    className="cpa-toolbar__btn cpa-toolbar__btn--danger"
                    disabled={!deletableSelection}
                    onClick={() => deletableSelection && setPendingDelete(deletableSelection)}
                  >
                    {t('paletteDelete')}
                  </button>

                  <span className="cpa-toolbar__gap" />

                  {/* Beyond the mock's palette, and secondary to it. 「插入到线上」 used
                      to sit here: a mode that turned every edge into a click target and
                      spliced a new question into it. Removed — it was a second way to
                      create both a node and two edges, parallel to ＋问题 and ＋规则 and
                      obeying neither's guards. */}
                  <button
                    type="button"
                    className="cpa-toolbar__btn cpa-toolbar__btn--alt"
                    disabled={!state.undoStack.length}
                    onClick={() => dispatch({ type: 'undo' })}
                  >
                    {t('paletteUndo')}
                  </button>
                </div>
              )}

              {context && draftMeta && context.headVersion > draftMeta.baseVersion && (
                <Alert type="warning" className="cpa-ct-alert" header={`已有新版本 v${context.headVersion}`}>
                  你的草稿基于 v{draftMeta.baseVersion}。保存时会检测冲突 —— 三路合并还没接上，
                  目前会返回 DecisionTreeConflictException。
                </Alert>
              )}

              {context && context.otherEditors.length > 0 && (
                <Alert type="info" className="cpa-ct-alert">
                  {context.otherEditors
                    .map((e) => `${e.owner}（基于 v${e.baseVersion}，${e.updatedAt.slice(11, 19)}）`)
                    .join('、')}{' '}
                  也在编辑这棵树。
                </Alert>
              )}

              {apiError && (
                <Alert type="error" dismissible className="cpa-ct-alert" onDismiss={() => setApiError(null)}>
                  {apiError}
                </Alert>
              )}

              {ruleMode && (
                <Alert
                  type="success"
                  dismissible
                  className="cpa-ct-alert"
                  onDismiss={() => setRuleMode(false)}
                >
                  从一张卡片下边的圆点拖到目标卡片，松手后填写条件。会成环或重复的目标不会接受连线。
                </Alert>
              )}

              {connectError && (
                <Alert
                  type="error"
                  dismissible
                  className="cpa-ct-alert"
                  onDismiss={() => setConnectError(null)}
                >
                  {connectError}
                </Alert>
              )}

              <div className="cpa-ct-pane cpa-ct-pane--canvas">
                <div className="cpa-canvas-host">
                  <TreeCanvas
                    projection={projection}
                    selectedNodeId={selectedId}
                    onSelectNode={setSelectedId}
                    selectedEdgeId={selectedEdgeId}
                    onSelectEdge={setSelectedEdgeId}
                    /*
                     * Draggable throughout edit mode. There used to be a 「自由拖动」
                     * toggle in front of this, on the reasoning that pinning a card
                     * should be a deliberate act — a dragged node leaves the derived
                     * layout for good. ＋问题 now writes a manual position itself (it
                     * drops the card at the viewport centre), so pinning is already
                     * part of ordinary editing and the toggle only stopped people
                     * moving a card they had just placed. 「重排」 is still how you
                     * give every card back to the layout.
                     */
                    draggable={editing && canEdit}
                    positionOverrides={state.positions}
                    // One gesture, one undo step — a drop can displace a cascade of
                    // cards, and undoing half of it would be worse than not undoing.
                    onNodesMoved={(positions) => dispatch({ type: 'set-positions', positions })}
                    focusNodeId={state.lastAddedNodeId}
                    newNodeId={state.lastAddedNodeId}
                    viewportCentreRef={viewportCentre}
                    /*
                     * Connectable only while ＋规则 is armed.
                     *
                     * It used to be live throughout edit mode, which made an edge the
                     * one thing on this canvas you could create by accident: the handles
                     * sit on the card border, so a drag that starts a few pixels off
                     * moves nothing and connects instead, and the result is a rule the
                     * author never described. Arming it costs one click and makes the
                     * handles mean something when they are lit.
                     */
                    connectable={editing && canEdit && ruleMode}
                    onConnectNodes={handleConnect}
                    /*
                     * Refuse the illegal pairs DURING the drag — React Flow will not
                     * complete the drop, so a cycle never becomes an edge that then has
                     * to be explained away.
                     */
                    canConnectNodes={(source, target) =>
                      connectionKind(state.draft, source, target).kind !== 'blocked'
                    }
                    onDisconnect={
                      editing && canEdit
                        ? (source, target, ruleIndex) =>
                            dispatch({
                              type: 'disconnect',
                              sourceNodeId: source,
                              targetNodeId: target,
                              ruleIndex,
                            })
                        : undefined
                    }
                    maxZoom={3}
                  />
                </div>

                {(selected || selectedEdge) && (
                  <aside className="cpa-side">
                    {selectedEdge ? (
                      <EdgeInspector
                        definition={state.draft}
                        edge={selectedEdge}
                        readOnly={!canEdit || !editing}
                        onSelectNode={(id) => {
                          setSelectedEdgeId(null);
                          setSelectedId(id);
                        }}
                        /*
                         * Resolved HERE, from the projection, rather than by rebuilding
                         * the id in the panel: the `e:src->tgt:i` format is the
                         * projection's, and a second copy of it would drift.
                         *
                         * Matched on `ruleIndex` (1-based) rather than on the trailing
                         * `:i` of the id, which is the index among the edges drawn to
                         * that target, not among the rules.
                         */
                        onSelectSibling={(sourceNodeId, ruleIndex) => {
                          const sib = projection.edges.find(
                            (e) =>
                              e.source === sourceNodeId &&
                              e.kind === 'when' &&
                              e.ruleIndex === ruleIndex + 1,
                          );
                          if (sib) setSelectedEdgeId(sib.id);
                        }}
                        onClose={() => setSelectedEdgeId(null)}
                        onSetCondition={(source, target, expression) =>
                          dispatch({
                            type: 'set-edge-condition',
                            sourceNodeId: source,
                            targetNodeId: target,
                            expression,
                          })
                        }
                      />
                    ) : (
                      <NodeInspector
                        definition={state.draft}
                        selected={selected}
                        issues={projection.issues}
                        readOnly={!canEdit || !editing}
                        onPatchNode={(nodeId, p) =>
                          dispatch({ type: 'patch-node', nodeId, patch: p })
                        }
                        onSelectNode={setSelectedId}
                        onClose={() => setSelectedId(null)}
                      />
                    )}
                  </aside>
                )}
              </div>

              {editing && (
                <div className="cpa-ct-bar cpa-ct-bar--foot">
                  {/*
                   * One button, always available: drop every hand-placed position AND
                   * turn on crossing reduction, i.e. hand the whole graph back to the
                   * optimal layout. It used to appear only when something had been
                   * moved, with the ordering behind a separate toggle — two controls
                   * for one intention, and the one you needed was hidden exactly when
                   * the canvas looked worst.
                   */}
                  <Button
                    iconName="refresh"
                    onClick={() => {
                      dispatch({ type: 'reset-positions' });
                      clearPositions(definitionId);
                      setOrdering('tidy');
                    }}
                  >
                    {movedCount > 0 ? t('tidyUpWithCount', { count: movedCount }) : t('tidyUp')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
      </div>

      {/* ── Delete, with its consequences stated first ─────────────────── */}
      <Modal
        visible={!!pendingDelete}
        onDismiss={() => setPendingDelete(null)}
        header={t('delTitle', { id: pendingDelete ?? '' })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPendingDelete(null)}>{t('cancel')}</Button>
              <Button
                variant="primary"
                disabled={!!deleteImpact?.blockedReason}
                onClick={() => {
                  if (pendingDelete) dispatch({ type: 'delete-node', nodeId: pendingDelete });
                  setSelectedId(null);
                  setPendingDelete(null);
                }}
              >
                {t('delConfirm')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {deleteImpact?.blockedReason ? (
          <Alert type="error">{deleteImpact.blockedReason}</Alert>
        ) : (
          <SpaceBetween size="m">
            <Box>
              {t('delRelink')}
              <strong> {deleteImpact?.relinkTo ?? t('delRelinkNone')}</strong>
            </Box>
            {deleteImpact?.brokenRules.length ? (
              <Alert
                type="warning"
                header={t('delBrokenTitle', { count: deleteImpact.brokenRules.length })}
              >
                <Box variant="p" padding={{ bottom: 'xs' }}>
                  <SafeTrans t={t} i18nKey="delBrokenBody" components={{ strong: <strong /> }} />
                </Box>
                <Table
                  variant="embedded"
                  items={deleteImpact.brokenRules}
                  columnDefinitions={[
                    {
                      id: 'n',
                      header: t('delColNode'),
                      width: 110,
                      cell: (r) => <code>{r.nodeId}</code>,
                    },
                    { id: 'k', header: t('delColKind'), width: 100, cell: (r) => r.kind },
                    {
                      id: 'e',
                      header: t('delColExpr'),
                      cell: (r) => (
                        <Box variant="code" fontSize="body-s">
                          {r.expression}
                        </Box>
                      ),
                    },
                  ]}
                />
              </Alert>
            ) : (
              <StatusIndicator type="success">{t('delNoBroken')}</StatusIndicator>
            )}
            {deleteImpact?.orphans.length ? (
              <Alert
                type="error"
                header={t('delOrphansTitle', { count: deleteImpact.orphans.length })}
              >
                {t('delOrphansBody')}
                {deleteImpact.orphans.map((o) => (
                  <code key={o} style={{ marginLeft: 6 }}>
                    {o}
                  </code>
                ))}
              </Alert>
            ) : null}
          </SpaceBetween>
        )}
      </Modal>

      {/*
        ── ＋规则: the ONE place an edge gets created ────────────────────
        A rule is {condition, target}. Both halves are stated here, which is the point:
        the drag gesture this replaced produced the target first and asked for the
        condition afterwards, so a half-made rule existed on the canvas in between.
      */}
      <Modal
        visible={!!pendingBranch}
        onDismiss={() => setPendingBranch(null)}
        header={t('brTitle', { id: pendingBranch ?? '' })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPendingBranch(null)}>{t('cancel')}</Button>
              <Button
                variant="primary"
                disabled={
                  !branchGuard?.ok ||
                  (branchTarget === 'existing'
                    ? !branchTargetId || (!branchIsUnconditional && !effectiveBranchExpr)
                    : !effectiveBranchExpr)
                }
                onClick={commitBranch}
              >
                {t('brConfirm')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {branchGuard && !branchGuard.ok ? (
          <Alert type="error">{branchGuard.reason}</Alert>
        ) : (
          <SpaceBetween size="m">
            <FormField label="规则指向" description="新建一道题，或者连到画布上已有的题">
              <RadioGroup
                direction="horizontal"
                value={branchTarget}
                onChange={({ detail }) => setBranchTarget(detail.value as 'new' | 'existing')}
                items={[
                  { value: 'new', label: '新建一道题' },
                  {
                    value: 'existing',
                    label: '已有的题',
                    // Nothing legal to point at is a real state — a leaf whose every
                    // other node would make a cycle. Disabled with the reason, rather
                    // than an empty picker.
                    disabled: branchTargets.length === 0,
                  },
                ]}
              />
            </FormField>

            {branchTarget === 'existing' && (
              <FormField label="目标问题" description="只列出不会形成环、也不会重复的目标">
                <Select
                  expandToViewport
                  filteringType="auto"
                  selectedOption={
                    branchTargetId
                      ? (branchTargets.find((o) => o.value === branchTargetId) ?? {
                          value: branchTargetId,
                        })
                      : null
                  }
                  placeholder="选择一道已有的题"
                  options={branchTargets}
                  onChange={({ detail }) => setBranchTargetId(detail.selectedOption.value ?? null)}
                />
              </FormField>
            )}

            {branchIsUnconditional ? (
              /*
               * Not a choice the author makes — `connectionKind` returns
               * `unconditional` exactly when the source has NO out-edge yet, because
               * the first way out of a question needs no condition to distinguish it
               * from anything. The earlier wording blamed the ANSWER OPTIONS, which is
               * a different rule and simply not true: a question with six unused
               * options still gets an unconditional first edge.
               */
              <Alert type="info" header="这条是无条件规则">
                这道题目前还没有任何出边，所以第一条规则无需条件 —— 走到这里就一定去目标题。
                等它有了第二条出边，两条都会需要条件；之后也可以在规则卡片里给这条补上条件。
              </Alert>
            ) : branchOptions.length > 0 ? (
              <FormField label={t('brPickLabel')} description={t('brPickHint')}>
                <Select
                  expandToViewport
                  selectedOption={
                    branchValue
                      ? {
                          value: branchValue,
                          label:
                            branchNode?.answerOptions?.find((o) => o.value === branchValue)
                              ?.optionText.cn ?? branchValue,
                        }
                      : null
                  }
                  placeholder={t('connPickPlaceholder')}
                  options={branchOptions.map((v) => ({
                    value: v,
                    label:
                      branchNode?.answerOptions?.find((o) => o.value === v)?.optionText.cn ?? v,
                    description: `value = ${v}`,
                  }))}
                  onChange={({ detail }) => setBranchValue(detail.selectedOption.value ?? null)}
                />
              </FormField>
            ) : (
              <FormField label={t('brExprLabel')} description={t('brExprHint')}>
                <Input
                  value={branchExpr}
                  placeholder="metric['Q_1_1_10'] >= 0.5"
                  onChange={({ detail }) => setBranchExpr(detail.value)}
                />
              </FormField>
            )}
            {effectiveBranchExpr && (
              <Box variant="code" fontSize="body-s">
                {effectiveBranchExpr}
              </Box>
            )}
            {branchNode?.nextNodeId && (
              <Alert type="warning" header={t('brRewriteTitle')}>
                <SafeTrans
                  t={t}
                  i18nKey="brRewriteBody"
                  components={{ code: <code />, strong: <strong /> }}
                />
              </Alert>
            )}
            {branchTarget === 'new' && <Alert type="info">{t('brNewNodeNote')}</Alert>}
          </SpaceBetween>
        )}
      </Modal>

      {/*
        What this save cannot take with it.
        The expressions are shown verbatim rather than counted: a count is a number nobody can
        check, while the expression is what the author recognises as the rule they wrote.
      */}
      <Modal
        visible={!!pendingUnconvertible}
        onDismiss={() => setPendingUnconvertible(null)}
        header="有规则无法保存到决策树文档里"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPendingUnconvertible(null)}>取消</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setPendingUnconvertible(null);
                  void commit();
                }}
              >
                仍然保存
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {pendingUnconvertible && (
          <SpaceBetween size="s">
            <Box>
              下面这 {pendingUnconvertible.length} 条表达式目前的文档模型表达不了（缺四种写法：
              对答案做算术、兜底链、对全部选项做 <code>hasAnyMatch</code>、<code>hasKey</code>
              ），保存后的版本里<strong>不会包含它们</strong>：
            </Box>
            <ul className="cpa-unconvertible">
              {pendingUnconvertible.map((u) => (
                <li key={`${u.ownerId}-${u.kind}-${u.index}`}>
                  <code>{u.ownerId}</code> · {u.kind === 'eligibility' ? '资格规则' : '跳转规则'}{' '}
                  #{u.index + 1}
                  <br />
                  <code>{u.expression}</code>
                </li>
              ))}
            </ul>
            <Alert type="info">
              它们不会丢：原始问卷定义仍然保留着，在文档模型补上这四种写法之前，那边才是准。
              这个对话框只是不让这件事悄悄发生。
            </Alert>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
